/**
 * Editor de la config de uptime monitoring del proyecto.
 *
 * El cli-api corre un cron cada 5 min que lee `project_monitors` (vía
 * DB_PANEL) y hace HTTP GET a la URL configurada. Resultado va a
 * `project_uptime_checks` y se sirve al cliente vía
 * GET /api/projects/:id/uptime en el portal.
 *
 * Acá sólo controlamos: URL, enabled, código esperado, intervalo.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { CheckCircle2, AlertCircle, Circle } from "lucide-react";
import { api } from "@/lib/api";

interface MonitorConfig {
  projectId: string;
  url: string | null;
  intervalMinutes: number;
  enabled: boolean;
  expectedStatus: number;
  lastCheckedAt: number | null;
  lastStatus: "up" | "down" | "degraded" | null;
  lastStatusCode: number | null;
  lastLatencyMs: number | null;
  lastError: string | null;
}

const STATUS_BADGE: Record<string, string> = {
  up: "badge-success",
  down: "badge-error",
  degraded: "badge-warning",
};
const STATUS_ICON: Record<string, ReactNode> = {
  up: <CheckCircle2 className="size-3.5" />,
  down: <AlertCircle className="size-3.5" />,
  degraded: <AlertCircle className="size-3.5" />,
};

export function MonitorEditor({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ monitor: MonitorConfig | null }>({
    queryKey: ["project", projectId, "monitor"],
    queryFn: () => api.get(`/projects/${projectId}/monitor`),
  });

  const monitor = data?.monitor ?? null;
  const [url, setUrl] = useState(monitor?.url ?? "");
  const [enabled, setEnabled] = useState(monitor?.enabled ?? true);
  const [expectedStatus, setExpectedStatus] = useState(monitor?.expectedStatus ?? 200);
  const [interval, setIntervalM] = useState(monitor?.intervalMinutes ?? 5);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (monitor) {
      setUrl(monitor.url ?? "");
      setEnabled(monitor.enabled);
      setExpectedStatus(monitor.expectedStatus);
      setIntervalM(monitor.intervalMinutes);
    }
  }, [monitor?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () =>
      api.put(`/projects/${projectId}/monitor`, {
        url: url.trim() || null,
        enabled,
        expectedStatus,
        intervalMinutes: interval,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId, "monitor"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading) {
    return (
      <div className="grid place-items-center py-6">
        <span className="loading loading-spinner loading-sm text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {monitor?.lastStatus ? (
          <span
            className={`badge ${STATUS_BADGE[monitor.lastStatus]} gap-1`}
            title={
              monitor.lastError ??
              (monitor.lastLatencyMs !== null ? `${monitor.lastLatencyMs} ms` : "")
            }
          >
            {STATUS_ICON[monitor.lastStatus]}
            {monitor.lastStatus === "up"
              ? "Up"
              : monitor.lastStatus === "down"
                ? "Down"
                : "Degradado"}
          </span>
        ) : (
          <span className="badge badge-ghost gap-1">
            <Circle className="size-3.5" /> Sin datos aún
          </span>
        )}
        {monitor?.lastCheckedAt && (
          <span className="text-xs text-base-content/55">
            hace {timeAgo(monitor.lastCheckedAt)}
          </span>
        )}
        {monitor?.lastLatencyMs !== null && monitor?.lastLatencyMs !== undefined && (
          <span className="text-xs text-base-content/55 tabular-nums">
            {monitor.lastLatencyMs} ms
          </span>
        )}
      </div>

      <label className="form-control w-full">
        <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
          URL a monitorear
        </span>
        <input
          type="url"
          className="input input-bordered input-sm w-full"
          placeholder="https://app.cliente.com/health"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <span className="text-xs text-base-content/55 mt-1">
          El cron va a hacer GET a esta URL cada {interval} min.
        </span>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Código HTTP esperado
          </span>
          <input
            type="number"
            min={100}
            max={599}
            className="input input-bordered input-sm w-full"
            value={expectedStatus}
            onChange={(e) => setExpectedStatus(parseInt(e.target.value, 10) || 200)}
          />
        </label>
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Intervalo (min)
          </span>
          <input
            type="number"
            min={1}
            max={60}
            className="input input-bordered input-sm w-full"
            value={interval}
            onChange={(e) => setIntervalM(parseInt(e.target.value, 10) || 5)}
          />
        </label>
      </div>

      <label className="cursor-pointer flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Monitor activo</span>
      </label>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="btn btn-primary btn-sm"
        >
          {save.isPending && <span className="loading loading-spinner loading-xs" />}
          Guardar
        </button>
        {saved && <span className="text-xs text-success">Guardado ✓</span>}
      </div>

      {monitor?.lastError && (
        <div className="text-xs text-error border border-error/30 bg-error/5 rounded p-2 break-words">
          Último error: {monitor.lastError}
        </div>
      )}
    </div>
  );
}

function timeAgo(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
